/**
 * useAgentEntitlements - Hook for checking agent access based on org entitlements
 *
 * Reads entitlements from OrgProvider context (populated by /auth/me).
 * No additional API calls needed - entitlements are included in org data.
 *
 * Usage:
 *   const { entitledAgents, isAgentEntitled } = useAgentEntitlements();
 *
 *   // Filter agents for display
 *   entitledAgents.map(agent => <AgentCard agent={agent} />)
 *
 *   // Check specific agent access
 *   if (!isAgentEntitled('ksfo-delay')) return <AccessDenied />;
 */

import { useMemo } from 'react';
import { useOrg } from '../providers/OrgProvider';
import { getAgentBySlug, getEnabledAgents } from '../agents';
import type { AgentDefinition } from '../agents/types';
import { getEnabledFeatures } from '../features';
import type { FeatureDefinition } from '../features';

interface UseAgentEntitlementsResult {
  /** Array of agent slugs the current org is entitled to */
  entitledSlugs: string[];
  /** Enabled agents filtered by org entitlements */
  entitledAgents: AgentDefinition[];
  /** Check if the current org has access to a specific agent */
  isAgentEntitled: (slug: string) => boolean;
  /** Enabled features filtered by org entitlements */
  entitledFeatures: FeatureDefinition[];
  /** Check if the current org has access to a specific feature */
  isFeatureEntitled: (slug: string) => boolean;
  /** Whether org data is still loading */
  isLoading: boolean;
}

export function useAgentEntitlements(): UseAgentEntitlementsResult {
  const { currentOrg, isLoading } = useOrg();

  // Get org's entitled agent slugs
  const entitledSlugs = useMemo(() => {
    return currentOrg?.agent_entitlements || [];
  }, [currentOrg?.agent_entitlements]);

  // Filter globally enabled agents by org entitlements. alwaysEntitled entries
  // (e.g. Alerts) are universal platform capabilities — they show for every org,
  // including orgs with zero entitlements (which otherwise see nothing: secure default).
  const entitledAgents = useMemo(() => {
    return getEnabledAgents().filter(
      (agent) => agent.alwaysEntitled === true || entitledSlugs.includes(agent.slug)
    );
  }, [entitledSlugs]);

  // Check if a specific agent is entitled
  const isAgentEntitled = useMemo(() => {
    return (slug: string): boolean => {
      return getAgentBySlug(slug)?.alwaysEntitled === true || entitledSlugs.includes(slug);
    };
  }, [entitledSlugs]);

  // Filter globally enabled features by org entitlements
  const entitledFeatures = useMemo(() => {
    const enabledFeatures = getEnabledFeatures();

    if (entitledSlugs.length === 0) {
      return [];
    }

    return enabledFeatures.filter((feature) => entitledSlugs.includes(feature.slug));
  }, [entitledSlugs]);

  // Check if a specific feature is entitled (must also be enabled in registry)
  const isFeatureEntitled = useMemo(() => {
    const enabledFeatureSlugs = new Set(getEnabledFeatures().map((f) => f.slug));
    return (slug: string): boolean => {
      return enabledFeatureSlugs.has(slug) && entitledSlugs.includes(slug);
    };
  }, [entitledSlugs]);

  return {
    entitledSlugs,
    entitledAgents,
    isAgentEntitled,
    entitledFeatures,
    isFeatureEntitled,
    isLoading
  };
}
